import type { CallNotificationItem, Incident } from '@/signalr';
import { CollaborationState, IncidentState } from '@/signalr';
import { getPsapView } from '@/features/incident-detail/model/incidentDomain';
import { orderIncidentsByCreatedTimeDescending } from './incidentListQueries';

export type IncidentListType = 'forwarded' | 'open' | 'closed';

export function getIncidentListType(incident: Incident, psapId: string): IncidentListType {
  const collaborationState = getPsapView(incident, psapId)?.collaborationState ?? null;

  if (incident.state === IncidentState.Closed || collaborationState === CollaborationState.Rejected) {
    return 'closed';
  }

  if (collaborationState === CollaborationState.Requested && incident.state === IncidentState.Open) {
    return 'forwarded';
  }

  return 'open';
}

function selectByType(
  incidents: readonly Incident[] | undefined,
  psapId: string,
  type: IncidentListType,
): Incident[] {
  if (incidents == null) {
    return [];
  }

  return orderIncidentsByCreatedTimeDescending(
    incidents.filter((incident) => getIncidentListType(incident, psapId) === type),
  );
}

export function selectForwardedIncidents(incidents: readonly Incident[] | undefined, psapId: string): Incident[] {
  return selectByType(incidents, psapId, 'forwarded');
}

export function selectCurrentlyOpenIncidents(incidents: readonly Incident[] | undefined, psapId: string): Incident[] {
  return selectByType(incidents, psapId, 'open');
}

export function selectRecentClosedIncidents(incidents: readonly Incident[] | undefined, psapId: string): Incident[] {
  return selectByType(incidents, psapId, 'closed');
}

export type CallWithIncidentId = {
  callNotification: CallNotificationItem;
  incidentId: string | null;
};

export function toCallsWithIncidentId(
  calls: readonly CallNotificationItem[] | undefined,
  openIncidents: readonly Incident[],
  forwardedIncidents: readonly Incident[],
): CallWithIncidentId[] | undefined {
  const candidates = orderIncidentsByCreatedTimeDescending([...openIncidents, ...forwardedIncidents]);

  return calls?.map((callNotification) => ({
    callNotification,
    incidentId: findIncidentForCall(candidates, callNotification)?.id ?? null,
  }));
}

function findIncidentForCall(
  candidates: readonly Incident[],
  call: CallNotificationItem,
): Incident | undefined {
  const callStarted = Date.parse(call.startTimeUtc);

  return candidates.find(
    (incident) =>
      incident.caller.phoneNumbers.includes(call.callId) &&
      (incident.closedTimeUtc === null || Date.parse(incident.closedTimeUtc) >= callStarted),
  );
}

export function selectIncidentMapPoints(incidents: readonly Incident[] | undefined): Incident[] | undefined {
  return incidents?.filter((incident) => incident.state === IncidentState.Open);
}
